const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const TOKEN_KEY = 'pc_token';
export const USER_KEY = 'pc_user';
export const BRIEFING_KEY = 'pc_briefing';

// In-memory fallback for environments where sessionStorage is blocked
// (e.g. strict corporate browser policies)
let _memToken: string | null = null;
let _memUser: string | null = null;

function storageSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* blocked */ }
  if (key === TOKEN_KEY) _memToken = value;
  if (key === USER_KEY)  _memUser  = value;
}

function storageGet(key: string): string | null {
  try {
    const v = sessionStorage.getItem(key);
    if (v !== null) return v;
  } catch { /* blocked */ }
  if (key === TOKEN_KEY) return _memToken;
  if (key === USER_KEY)  return _memUser;
  return null;
}

function storageRemove(key: string): void {
  try { sessionStorage.removeItem(key); } catch { /* blocked */ }
  if (key === TOKEN_KEY) _memToken = null;
  if (key === USER_KEY)  _memUser  = null;
}

export function getToken(): string | null {
  return storageGet(TOKEN_KEY);
}

/**
 * The authenticated user, as stored in localStorage under `pc_user`.
 *
 * Named and exported rather than written inline, because `role` is the ONLY
 * thing standing between a client user and admin-only UI (bearing-health
 * component breakdown, the PeakVue trend chart, Alarm Manager, Users,
 * Databases). Every one of those gates is `getUser()?.role === 'admin'`, so if
 * this shape is ever widened or lost, the gates stop being checkable and
 * nothing complains.
 *
 * Fields mirror the JWT claims the backend issues.
 */
export interface AppUser {
  id?: number;
  name: string;
  email: string;
  role: string;
  company_id?: number | null;
  company_name?: string | null;
}

export function getUser(): AppUser | null {
  try {
    const raw = storageGet(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearAuth() {
  storageRemove(TOKEN_KEY);
  storageRemove(USER_KEY);
  storageRemove(BRIEFING_KEY);
  try { sessionStorage.removeItem(BRIEFING_KEY); } catch { /* blocked */ }
}

// Event the app listens for to route to the login screen when the session
// expires mid-use. See UNAUTHORIZED_EVENT handling in apiFetch.
export const UNAUTHORIZED_EVENT = 'pc:unauthorized';

// Re-entrancy guard: a token expiry makes EVERY in-flight request 401 at once
// (a page mount fires several). Without this, each 401 used to call
// window.location.reload(); because the selected machine/point is persisted, the
// reloaded page re-fired the same failing requests -> infinite reload loop that
// wedged the whole app. We now clear auth and route to login EXACTLY ONCE per
// expiry; the flag is reset on the next successful login.
let _sessionExpiredHandled = false;

export function resetUnauthorizedGuard() {
  _sessionExpiredHandled = false;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

/**
 * The one place auth, the 401 dance, and error translation happen.
 *
 * Extracted when file upload and download arrived: both need the SAME session
 * handling and NEITHER can use apiFetch, because it hardcodes a JSON
 * Content-Type (a multipart body must be typed by the browser, which alone knows
 * the boundary) and always parses the reply as JSON (a PDF is not). Copying
 * apiFetch twice would have made three copies of the re-entrancy guard below —
 * the one piece of this file whose absence wedged the whole app in a reload loop.
 *
 * `json` controls only whether the request BODY is declared as JSON.
 */
async function _request(
  path: string,
  options: RequestInit,
  json: boolean,
): Promise<Response> {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    cache: 'no-store',
    ...options,
    headers: {
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (res.status === 401) {
    // Route to login ONCE, without a full-page reload (a reload re-enters the
    // same failing fetch via the persisted machine/point selection -> loop).
    if (!_sessionExpiredHandled) {
      _sessionExpiredHandled = true;
      clearAuth();
      try { window.dispatchEvent(new Event(UNAUTHORIZED_EVENT)); } catch { /* non-browser */ }
    }
    throw new ApiError(401, 'Unauthenticated');
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch {
      /* ignore parse error */
    }
    throw new ApiError(res.status, detail);
  }

  return res;
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await _request(path, options, true);

  // 204 No Content — no body to parse
  if (res.status === 204) return undefined as unknown as T;

  return res.json() as Promise<T>;
}

/**
 * Multipart upload. Deliberately does NOT set Content-Type: for a FormData body
 * the browser must write it itself, because only it knows the multipart boundary
 * it generated. Setting 'multipart/form-data' by hand omits the boundary and the
 * server rejects the body as malformed — a failure that reads like a bad file.
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await _request(path, { method: 'POST', body: form }, false);
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

/**
 * Fetch binary content (report PDF attachments) through the authenticated API.
 *
 * A plain <a href> cannot be used: the download endpoint requires the bearer
 * token, and an anchor sends no Authorization header. The caller turns this into
 * an object URL.
 */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const res = await _request(path, {}, false);
  return res.blob();
}

export async function apiLogin(email: string, password: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'Invalid credentials');
  }
  const data = await res.json();
  storageSet(TOKEN_KEY, data.access_token);
  const u = data.user || data;
  storageSet(USER_KEY, JSON.stringify({
    name: u.name,
    email: u.email || email,
    role: u.role,
  }));
  // Fresh session — re-arm the 401 handler so a future expiry routes to login again.
  resetUnauthorizedGuard();
}
